import { useMemo, useState } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconFilter } from '@sorare/core/src/atoms/icons/system/iconFilter.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { GQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import type { CardHit as CardHitType } from '@sorare/core/src/lib/algolia';
import { convertToCardHit } from '@sorare/core/src/lib/algolia';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';

import {
  TRADE_SORTS,
  useBackendTradeCardsSearch,
} from '#src/hooks/search/useBackendTradeCardsSearch.ts';
import { BackendClearAllFilters } from '#src/search/ClearAllFilters/index.tsx';
import { FiltersManager } from '#src/search/FiltersManager/index.tsx';
import { BackendFiltersManagerDialog } from '#src/search/FiltersManagerDialog/index.tsx';
import { BackendSearchBox } from '#src/search/SearchBox/index.tsx';
import { BackendSortCards } from '#src/searchCards/BackendSortCards/index.tsx';
import { backendTradeFilters as backendFootballTradeFilters } from '#src/searchCards/football/cardFilters.tsx';
import { backendTradeFilters as backendBaseballTradeFilters } from '#src/searchCards/mlb/cardFilters.tsx';
import { backendTradeFilters as backendNbaTradeFilters } from '#src/searchCards/nba/cardFilters.tsx';

import type { CardSelectionProps } from './CardPickerResults.tsx';
import { CardPickerResults, FilterColumn, Row } from './CardPickerResults.tsx';

const backendTradeFiltersBySport: Partial<Record<Sport, () => FiltersGroup[]>> =
  {
    [Sport.FOOTBALL]: backendFootballTradeFilters,
    [Sport.NBA]: backendNbaTradeFilters,
    [Sport.BASEBALL]: backendBaseballTradeFilters,
  };

export const supportsBackendTradesPicker = (sport: Sport) =>
  sport in backendTradeFiltersBySport;

type Props = CardSelectionProps & {
  owner: { id: string; slug: string };
  tradeSport: Sport;
};

type SearchCardsPage = ReturnType<
  typeof useBackendTradeCardsSearch
>['searchCards'];

export const BackendCardPickerContent = ({
  owner,
  tradeSport,
  selectedCards,
  setSelectedCards,
  maxCards,
  fetchMissingCardsData,
  cardsData,
}: Props) => {
  const isLaptop = useIsLaptop();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const cardFilters = useMemo(
    () => backendTradeFiltersBySport[tradeSport]?.() ?? [],
    [tradeSport]
  );

  const { gqlSearchContextValue, searchCards, loading, page, setSearchParams } =
    useBackendTradeCardsSearch({
      sport: tradeSport,
      userSlug: owner.slug,
      cardFilters,
    });

  const [accumulated, setAccumulated] = useState<{
    source: SearchCardsPage;
    items: CardHitType[];
  }>({ source: undefined, items: [] });

  if (searchCards && accumulated.source !== searchCards) {
    const pageItems = searchCards.hits.map(convertToCardHit);
    const previousItems =
      searchCards.page === 1
        ? []
        : accumulated.items.filter(
            item => !pageItems.some(pageItem => pageItem.slug === item.slug)
          );
    setAccumulated({
      source: searchCards,
      items: [...previousItems, ...pageItems],
    });
  }

  const loadNextPage = () => {
    setSearchParams(previous => {
      previous.set('p', (page + 1).toString());
      return previous;
    });
  };

  return (
    <GQLSearchContext value={gqlSearchContextValue}>
      <Vertical gap={2}>
        <Horizontal>
          <BackendSearchBox />
          {!!isLaptop && <BackendSortCards indexes={TRADE_SORTS} />}
          {!isLaptop && (
            <IconButton
              size="small"
              color="quaternary"
              icon={iconFilter}
              onClick={() => setFilterDialogOpen(true)}
            />
          )}
        </Horizontal>

        <Row gap={2}>
          {isLaptop ? (
            <FilterColumn>
              <FiltersManager filters={cardFilters} skipClearAllButton />
              <BackendClearAllFilters />
            </FilterColumn>
          ) : (
            <BackendFiltersManagerDialog
              open={filterDialogOpen}
              onClose={() => setFilterDialogOpen(false)}
              filtersGroups={cardFilters}
            />
          )}
          <CardPickerResults
            items={accumulated.items}
            hasMore={page < (searchCards?.nbPages ?? 0)}
            searching={loading}
            onLoadMore={loadNextPage}
            selectedCards={selectedCards}
            setSelectedCards={setSelectedCards}
            maxCards={maxCards}
            fetchMissingCardsData={fetchMissingCardsData}
            cardsData={cardsData}
          />
        </Row>
      </Vertical>
    </GQLSearchContext>
  );
};
